import { useToast } from "@/components/ui/use-toast";
import {
  useSupportChatMuteStore,
  useSupportChatMuted,
} from "@/lib/hooks/conversations/use-support-chat-mute-store";
import { cn } from "@/lib/utils";
import { Bell, BellOff } from "lucide-react";

interface SupportChatMuteBarProps {
  /** Current user, so the mute stays scoped to this account. */
  userId: string | null;
  className?: string;
}

/**
 * Bottom bar for the support chat surfaces. Toggles a persistent mute that
 * silences support-chat notifications until midnight ET. Data-agnostic: the
 * caller supplies the current user id.
 */
export function SupportChatMuteBar({
  userId,
  className,
}: SupportChatMuteBarProps) {
  const { toast } = useToast();
  const isMuted = useSupportChatMuted(userId);
  const mute = useSupportChatMuteStore((s) => s.mute);
  const unmute = useSupportChatMuteStore((s) => s.unmute);

  const handleToggle = () => {
    if (isMuted) {
      unmute();
      return;
    }
    mute(userId);
    toast({
      description: "Support chat conversations muted until tomorrow.",
    });
  };

  return (
    <div className={cn("border-t border-[#cbc6be] p-4", className)}>
      <button
        type="button"
        onClick={handleToggle}
        aria-pressed={isMuted}
        aria-label={
          isMuted
            ? "Unmute support chat notifications"
            : "Mute support chat notifications until tomorrow"
        }
        className="flex items-center gap-2 text-sm text-text-secondary"
      >
        {isMuted ? <BellOff className="size-6" /> : <Bell className="size-6" />}
        <span>{isMuted ? "Muted until tomorrow" : "Notifications"}</span>
      </button>
    </div>
  );
}
